import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Ensure cookies are sent
axios.defaults.withCredentials = true;
const API_BASE = "https://api.zapexpenses.com";

import DeleteConfirmationModal from './DeleteConfirmationModal';

const MileagePage = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'pending', 'approved'
    const [editingId, setEditingId] = useState(null); // ID of log being edited
    const [editValues, setEditValues] = useState({}); // Edited values for the log
    const [saving, setSaving] = useState(false);
    const [expandedIds, setExpandedIds] = useState(new Set()); // IDs of expanded logs

    // Delete functionality
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [logToDelete, setLogToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const checkDelete = (log, e) => {
        if (e) e.stopPropagation();
        setLogToDelete(log);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!logToDelete) return;
        setIsDeleting(true);
        try {
            // Backend endpoint: /mileage_logs/:id (DELETE)
            await axios.delete(`${API_BASE}/mileage_logs/${logToDelete.id}`);

            // Remove from list
            setLogs(logs.filter(l => l.id !== logToDelete.id));
            setDeleteModalOpen(false);
            setLogToDelete(null);

            // If viewing single log and deleted, redirect
            if (filterId) {
                window.location.href = '/mileage';
            }
        } catch (err) {
            console.error("Delete failed", err);
            alert("Failed to delete mileage log. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    // Parse URL query parameter for single-log filtering
    const filterId = new URLSearchParams(window.location.search).get('id');

    useEffect(() => {
        fetchLogs();
    }, []);

    // Auto-open edit mode if filterId is present and log is pending
    useEffect(() => {
        if (filterId && logs.length > 0 && !editingId) {
            const targetLog = logs.find(l => String(l.id) === String(filterId));
            if (targetLog && targetLog.status === 'pending') {
                startEdit(targetLog);
            }
        }
    }, [logs, filterId, editingId]);

    const fetchLogs = async () => {
        try {
            // If ?id=X is present, fetch that specific log; otherwise fetch all
            const endpoint = filterId
                ? `${API_BASE}/mileage_logs?id=${filterId}`
                : `${API_BASE}/mileage_logs`;
            const res = await axios.get(endpoint);
            setLogs(res.data);
            setError(null);
        } catch (err) {
            console.error(err);
            setError("Failed to load mileage logs. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Toggle expansion of a log
    const toggleExpand = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Start editing a log
    const startEdit = (log, e) => {
        if (e) e.stopPropagation(); // Prevent row toggle
        setEditingId(log.id);
        setEditValues({
            origin: log.origin || '',
            destination: log.destination || '',
            purpose: log.purpose || '',
            miles: log.miles || log.distance || '',
            date: log.date || '',
            vehicle: log.vehicle || '',
            rate: log.rate || ''
        });
    };

    // Cancel editing
    const cancelEdit = () => {
        setEditingId(null);
        setEditValues({});
    };

    // Save edits
    const saveEdit = async (logId) => {
        setSaving(true);
        try {
            await axios.post(`${API_BASE}/mileage_logs/fix/${logId}`, editValues);
            // Update local state to reflect changes
            setLogs(logs.map(log =>
                log.id === logId
                    ? { ...log, ...editValues, status: 'approved' }
                    : log
            ));
            setEditingId(null);
            setEditValues({});
        } catch (err) {
            console.error(err);
            const errorMsg = err.response?.data?.detail || err.message || "Unknown error";
            alert(`Failed to save changes. Error: ${errorMsg}`);
        } finally {
            setSaving(false);
        }
    };

    // Handle input changes during edit
    const handleEditChange = (field, value) => {
        setEditValues(prev => ({ ...prev, [field]: value }));
    };

    // Filter logs based on active filter tab
    const getFilteredLogs = () => {
        if (filterId) return logs; // If viewing single log, show it regardless of filter
        switch (activeFilter) {
            case 'pending':
                return logs.filter(l => l.status === 'pending');
            case 'approved':
                return logs.filter(l => l.status === 'approved');
            default:
                return logs;
        }
    };

    // Group logs by status for counts
    const pendingLogs = logs.filter(l => l.status === 'pending');
    const approvedLogs = logs.filter(l => l.status === 'approved');

    if (loading) return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
            <div className="text-indigo-500 animate-pulse">Loading mileage logs...</div>
        </div>
    );

    const filteredLogs = getFilteredLogs();

    return (
        <div className="max-w-5xl mx-auto p-6 md:py-12">
            <header className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-white mb-2">
                        {filterId ? 'Mileage Details' : 'My Mileage'}
                    </h1>
                    <p className="text-slate-400">
                        {filterId
                            ? `Viewing mileage log ID: ${filterId}`
                            : 'Track and claim your business travel.'}
                    </p>
                </div>

                {/* Filter Tabs (only show when not viewing single log) */}
                {!filterId && (
                    <div className="flex bg-slate-900/50 backdrop-blur-md border border-white/5 rounded-full px-1 p-1">
                        <button
                            onClick={() => setActiveFilter('all')}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${activeFilter === 'all'
                                ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/25'
                                : 'text-slate-400 hover:text-white'
                                }`}
                        >
                            All ({logs.length})
                        </button>
                        <button
                            onClick={() => setActiveFilter('pending')}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${activeFilter === 'pending'
                                ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/25'
                                : 'text-slate-400 hover:text-white'
                                }`}
                        >
                            Pending ({pendingLogs.length})
                        </button>
                        <button
                            onClick={() => setActiveFilter('approved')}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${activeFilter === 'approved'
                                ? 'bg-green-500 text-white shadow-lg shadow-green-500/25'
                                : 'text-slate-400 hover:text-white'
                                }`}
                        >
                            Approved ({approvedLogs.length})
                        </button>
                    </div>
                )}
            </header>

            {/* Back to all mileage link when viewing single */}
            {filterId && (
                <div className="mb-6">
                    <a
                        href="/mileage"
                        className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors text-sm font-medium"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                        </svg>
                        Back to all mileage
                    </a>
                </div>
            )}

            {error ? (
                <div className="bg-red-500/10 border border-red-500/20 text-red-200 p-6 rounded-xl text-center">
                    {error}
                </div>
            ) : filteredLogs.length === 0 ? (
                <div className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-200 p-12 rounded-2xl text-center">
                    <div className="text-4xl mb-4">🚗</div>
                    <h3 className="text-xl font-bold mb-2">
                        {activeFilter === 'pending' ? 'No pending mileage' :
                            activeFilter === 'approved' ? 'No approved mileage' :
                                'No mileage logged'}
                    </h3>
                    <p className="text-indigo-300/70">
                        {activeFilter === 'pending'
                            ? 'All your mileage logs have been reviewed!'
                            : activeFilter === 'approved'
                                ? 'Approve some mileage logs to see them here.'
                                : 'Text your trips to your inbox to auto-log mileage.'}
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredLogs.map((log) => {
                        const isEditing = editingId === log.id;
                        const isPending = log.status === 'pending';
                        const isExpanded = expandedIds.has(log.id);

                        return (
                            <div
                                key={log.id}
                                onClick={() => !isEditing && toggleExpand(log.id)}
                                className={`bg-slate-900 border rounded-xl p-3 md:p-4 transition-all ${isPending ? 'border-amber-500/30' : 'border-slate-800'}
                                ${isEditing ? 'ring-2 ring-indigo-500/50' : 'hover:bg-slate-800/50 cursor-pointer'}
                                ${isExpanded ? 'bg-slate-800/30 ring-1 ring-white/5' : ''}`}
                            >

                                {isEditing ? (
                                    /* Edit Mode */
                                    <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center gap-2 mb-4">
                                            <span className="text-indigo-400 font-bold text-sm uppercase tracking-wide">Editing Mileage Log</span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Read-only Context Info */}
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Vehicle</label>
                                                <input
                                                    type="text"
                                                    value={editValues.vehicle}
                                                    onChange={(e) => handleEditChange('vehicle', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Rate (£/mi)</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={editValues.rate}
                                                    onChange={(e) => handleEditChange('rate', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Date</label>
                                                <input
                                                    type="date"
                                                    value={editValues.date}
                                                    onChange={(e) => handleEditChange('date', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Distance (miles)</label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={editValues.miles}
                                                    onChange={(e) => handleEditChange('miles', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Origin</label>
                                                <input
                                                    type="text"
                                                    value={editValues.origin}
                                                    onChange={(e) => handleEditChange('origin', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Destination</label>
                                                <input
                                                    type="text"
                                                    value={editValues.destination}
                                                    onChange={(e) => handleEditChange('destination', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Purpose</label>
                                                <input
                                                    type="text"
                                                    value={editValues.purpose}
                                                    onChange={(e) => handleEditChange('purpose', e.target.value)}
                                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white focus:outline-none focus:border-indigo-500 transition-colors"
                                                />
                                            </div>
                                        </div>

                                        <div className="flex gap-3 mt-4">
                                            <button
                                                onClick={() => saveEdit(log.id)}
                                                disabled={saving}
                                                className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition font-semibold disabled:opacity-50"
                                            >
                                                {saving ? 'Saving...' : 'Approve & Save'}
                                            </button>
                                            <button
                                                onClick={cancelEdit}
                                                className="px-4 py-2 bg-slate-800 text-slate-300 rounded hover:bg-slate-700 transition font-medium"
                                            >
                                                Cancel
                                            </button>

                                            <button
                                                onClick={(e) => checkDelete(log, e)}
                                                className="px-3 py-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                                title="Delete Log"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    /* View Mode */
                                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                                        {/* Icon / Date Block */}
                                        <div className="flex flex-row md:flex-col items-center md:items-center gap-2 md:w-16 shrink-0">
                                            <div className="text-2xl">
                                                🚗
                                            </div>
                                            <div className="text-slate-400 text-sm font-mono text-center font-semibold">
                                                {new Date(log.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                            </div>
                                        </div>

                                        {/* Details - Uniform Key-Value Grid */}
                                        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-2 gap-x-6 w-full items-start">

                                            {/* 1. Route */}
                                            <div className="col-span-1 sm:col-span-2">
                                                <span className="text-slate-500 font-bold uppercase text-[10px] tracking-wider mr-2">Route:</span>
                                                <span className="text-white text-sm font-medium break-words">
                                                    {log.origin}
                                                    <span className="text-slate-500 mx-1">→</span>
                                                    {log.destination}
                                                </span>
                                            </div>

                                            {/* 2. Distance */}
                                            <div>
                                                <span className="text-slate-500 font-bold uppercase text-[10px] tracking-wider mr-2">Distance:</span>
                                                <span className="text-white text-sm">{log.miles || log.distance} mi</span>
                                            </div>

                                            {/* 3. Purpose */}
                                            <div className="col-span-1 sm:col-span-2">
                                                <span className="text-slate-500 font-bold uppercase text-[10px] tracking-wider mr-2">Purpose:</span>
                                                <span className="text-white text-sm">{log.purpose}</span>
                                            </div>

                                            {/* 4. Vehicle */}
                                            {log.vehicle && (
                                                <div>
                                                    <span className="text-slate-500 font-bold uppercase text-[10px] tracking-wider mr-2">Vehicle:</span>
                                                    <span className="text-white text-sm">{log.vehicle}</span>
                                                </div>
                                            )}

                                            {/* 5. Claim (Prominent at bottom) + Rate (Subtle) */}
                                            <div className="col-span-1 sm:col-span-2 lg:col-span-4 mt-1 pt-2 border-t border-white/5 flex items-baseline gap-2">
                                                <span className="text-slate-400 font-extrabold uppercase text-xs tracking-wider mr-1">Claim:</span>
                                                <span className="text-white text-lg font-bold">£{parseFloat(log.reclaim_amount || 0).toFixed(2)}</span>
                                                {log.rate && (
                                                    <span className="text-slate-500 text-xs">at £{log.rate}/mi</span>
                                                )}
                                            </div>


                                            {/* Actions (Edit/Status/Delete) */}
                                            <div className="col-span-1 sm:col-span-2 lg:col-span-4 flex justify-end items-center gap-3">
                                                {isPending ? (
                                                    <button
                                                        onClick={() => startEdit(log)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 text-amber-500 rounded text-xs font-bold border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                                                    >
                                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                                                        Edit / Fix
                                                    </button>
                                                ) : (
                                                    <div className="flex items-center gap-3">
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-500/10 text-green-500 rounded text-xs font-bold border border-green-500/20">
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                                            </svg>
                                                            Approved
                                                        </span>
                                                        <a href={`/review?id=${log.id}&type=mileage`} className="text-slate-500 hover:text-indigo-400 transition-colors flex items-center gap-1 text-xs font-semibold" title="Edit Log">
                                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                                                            </svg>
                                                            Edit
                                                        </a>
                                                    </div>
                                                )}

                                                <button
                                                    onClick={(e) => checkDelete(log, e)}
                                                    className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                                    title="Delete Log"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}


            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                isDeleting={isDeleting}
                title="Delete Mileage Log?"
                message="Are you sure you want to delete this mileage log? It will be removed from your list."
            />
        </div>
    );
};

export default MileagePage;
